/** Firmware mock data and color-aware version filtering. */

import type { FirmwareMockItem, FirmwareTab } from './firmware-types'
import { FIRMWARE_TABS, FIRMWARE_FOLDER_MAP, VERSION_KEY_MAP } from './firmware-types'
import { getDeviceColorName } from '@/api/device/protocol'

const FACTORY_ACCOUNT_UUID = '1056752647'

const MOCK_FIRMWARE_DATA: Record<FirmwareTab, FirmwareMockItem> = {
  earphone: {
    deviceName: 'Headset Main', latestVersion: '', size: '796 KB', description: 'Headset firmware update.',
    historyList: [
      { version: 'V1.1.11', fileName: 'G1_Headset_White_260825_V1.1.11.up', color: 'white', factoryOnly: false, description: 'Headset firmware update.', update_date: '2026.08.25 10:12' },
      { version: 'V1.1.11', fileName: 'G1_Headset_Black_260825_V1.1.11.up', color: 'black', factoryOnly: false, description: 'Headset firmware update.', update_date: '2026.08.25 10:13' },
      { version: 'V1.1.10', fileName: 'G1_Headset_White_260805_V1.1.10.up', color: 'white', description: 'Headset firmware update.', update_date: '2026.08.05 17:36' },
      { version: 'V1.1.10', fileName: 'G1_Headset_Black_260805_V1.1.10.up', color: 'black', description: 'Headset firmware update.', update_date: '2026.08.05 17:37' },
      { version: 'V1.1.9', fileName: 'G1_Headset_Black_260801_V1.1.9.up', color: 'black', description: 'Headset firmware update.', update_date: '2026.08.01 11:14' },
      { version: 'V1.1.9', fileName: 'G1_Headset_White_260801_V1.1.9.up', color: 'white', description: 'Headset firmware update.', update_date: '2026.08.01 11:14' },
    ],
  },
  earphoneBluetooth: {
    deviceName: 'Headset Bluetooth', latestVersion: '', size: '777 KB', description: 'Improved pairing speed with multi-device support.',
    historyList: [
      { version: 'V1.1.3', fileName: 'G1_Bluetooth_260824_V1.1.3.up', factoryOnly: false, description: 'Improved pairing speed with multi-device support.', update_date: '2026.08.24 10:15' },
      { version: 'V1.1.2', fileName: 'G1_Bluetooth_260714_V1.1.2.up', description: 'Enhanced Bluetooth connection stability.', update_date: '2026.07.14 11:03' },
    ],
  },
  earphoneNoiseCancel: {
    deviceName: 'Headset ENC', latestVersion: '', size: '453 KB', description: 'Advanced noise cancellation logic for office environments.',
    historyList: [
      { version: 'V1.1.2', fileName: 'G1_Noise_260724_V1.1.2.up', description: 'Advanced noise cancellation logic for office environments.', update_date: '2026.07.24 21:23' },
      { version: 'V1.1.1', fileName: 'G1_Noise_260714_V1.1.1.up', description: 'Initial wind-noise reduction algorithm upgrade.', update_date: '2026.07.14 11:03' },
    ],
  },
  dongle: {
    deviceName: 'Dongle', latestVersion: '', size: '496 KB', description: 'Fixed known stability issues on specific PC chipsets.',
    historyList: [
      { version: 'V1.1.10', fileName: 'G1_Dongle_White_260825_V1.1.10.up', color: 'white', factoryOnly: false, description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.25 10:10' },
      { version: 'V1.1.10', fileName: 'G1_Dongle_Black_260825_V1.1.10.up', color: 'black', factoryOnly: false, description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.25 10:11' },
      { version: 'V1.1.9', fileName: 'G1_Dongle_Black_260805_V1.1.9.up', color: 'black', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.05 16:38' },
      { version: 'V1.1.9', fileName: 'G1_Dongle_White_260805_V1.1.9.up', color: 'white', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.05 16:35' },
      { version: 'V1.1.8', fileName: 'G1_Dongle_Black_260801_V1.1.8.up', color: 'black', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.01 11:15' },
      { version: 'V1.1.8', fileName: 'G1_Dongle_White_260801_V1.1.8.up', color: 'white', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.01 11:15' },
    ],
  },
}

export function parseFirmwareVersion(version: string): number[] {
  const raw = String(version || '').trim().replace(/^[vV]/, '')
  if (!raw) return []
  const main = raw.split(/[-_]/)[0]
  const suffix = raw.slice(main.length).replace(/^[-_]/, '')
  const nums = main.split('.').map(n => parseInt(n, 10) || 0)
  if (suffix) nums.push(parseInt(suffix, 10) || 0)
  return nums
}

export function compareFirmwareVersion(v1: string, v2: string): number {
  const a = parseFirmwareVersion(v1); const b = parseFirmwareVersion(v2)
  const len = Math.max(a.length, b.length)
  for (let i = 0; i < len; i++) {
    if ((a[i] || 0) > (b[i] || 0)) return 1
    if ((a[i] || 0) < (b[i] || 0)) return -1
  }
  return 0
}

function normalizeColor(value: unknown): 'black' | 'white' | '' {
  const color = getDeviceColorName(value)
  return color === 'black' || color === 'white' ? color : ''
}

function getUserUuid(userUuid?: string | null): string {
  if (userUuid != null) return String(userUuid).trim()
  try { return String(localStorage.getItem('uuid') || '').trim() } catch { return '' }
}

function isFactoryAccount(userUuid?: string | null): boolean {
  return getUserUuid(userUuid) === FACTORY_ACCOUNT_UUID
}

function getDeviceColor(tab: FirmwareTab, detail?: Record<string, unknown> | null): 'black' | 'white' | '' {
  if (!detail) return ''
  const value = tab === 'dongle' ? (detail.colorName ?? detail.color) : (detail.headsetColorName ?? detail.headsetColor)
  return normalizeColor(value)
}

function parseFirmwareFileName(fileName: string): { color: 'black' | 'white' | ''; version: string } {
  const name = String(fileName || '').trim()
  const match = name.match(/(?:^|_)(v\d+(?:\.\d+)*(?:[-_]\d+)?)\.up$/i)
  let color: 'black' | 'white' | '' = ''
  for (const segment of name.replace(/\.up$/i, '').split(/[_.]/)) {
    const parsed = normalizeColor(segment)
    if (parsed) { color = parsed; break }
  }
  return { color, version: match ? `V${match[1].replace(/^[vV]/, '')}` : '' }
}

function buildFirmwareUrl(tab: FirmwareTab, version: string, fileName?: string, detail?: Record<string, unknown> | null, userUuid?: string | null): string {
  const folder = FIRMWARE_FOLDER_MAP[tab]
  const v = String(version || '').trim()
  if (!folder || !v) return ''
  const rawItems = (MOCK_FIRMWARE_DATA[tab] || MOCK_FIRMWARE_DATA.earphone).historyList
  const item = fileName
    ? rawItems.find(entry => entry.fileName === fileName)
    : rawItems.find(entry => String(entry.version || '').replace(/^[vV]/, 'V') === v)
  const base = import.meta.env.BASE_URL || '/'
  return `${base}upgrading/${folder}/${item?.fileName || `${v}.up`}`
}

function normalizeFirmwareItem(tab: FirmwareTab, item: FirmwareMockItem['historyList'][number]) {
  const fileName = String(item.fileName || '').trim()
  const parsed = parseFirmwareFileName(fileName)
  return { ...item, version: parsed.version || String(item.version || '').replace(/^[vV]/, 'V'), fileName, color: parsed.color || item.color, downloadUrl: item.downloadUrl || buildFirmwareUrl(tab, parsed.version || item.version, fileName) }
}

function getVisibleRawList(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null) {
  const factory = isFactoryAccount(userUuid)
  return (MOCK_FIRMWARE_DATA[tab] || MOCK_FIRMWARE_DATA.earphone).historyList.filter(item => factory || !item.factoryOnly).map(item => normalizeFirmwareItem(tab, item))
}

export function getFirmwareHistoryList(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null) {
  const list = getVisibleRawList(tab, detail, userUuid)
  if (isFactoryAccount(userUuid) || (tab !== 'earphone' && tab !== 'dongle')) return list
  const deviceColor = getDeviceColor(tab, detail)
  return deviceColor ? list.filter(item => !item.color || item.color === deviceColor) : []
}

function getColorMatchedListForLatest(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null) {
  const list = getVisibleRawList(tab, detail, userUuid)
  if (tab !== 'earphone' && tab !== 'dongle') return list
  const deviceColor = getDeviceColor(tab, detail)
  if (deviceColor) return list.filter(item => !item.color || item.color === deviceColor)
  return isFactoryAccount(userUuid) ? list : []
}

export function getFirmwareMockData(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null): FirmwareMockItem {
  const data = MOCK_FIRMWARE_DATA[tab] || MOCK_FIRMWARE_DATA.earphone
  const historyList = getFirmwareHistoryList(tab, detail, userUuid)
  const latest = getColorMatchedListForLatest(tab, detail, userUuid).slice().sort((a, b) => compareFirmwareVersion(b.version, a.version))[0]
  return { ...data, latestVersion: latest?.version || '', latestFileName: latest?.fileName || '', latestDownloadUrl: latest?.downloadUrl || '', description: latest?.description || '', historyList }
}

export function getDeviceCurrentVersion(tab: FirmwareTab, detail?: Record<string, unknown> | null): string {
  const version = detail?.[VERSION_KEY_MAP[tab]]
  return typeof version === 'string' && version.trim() ? version.trim() : ''
}

export function hasFirmwareUpdate(tab: FirmwareTab, detail?: Record<string, unknown> | null, userUuid?: string | null): boolean {
  const current = getDeviceCurrentVersion(tab, detail)
  const latest = getFirmwareMockData(tab, detail, userUuid).latestVersion
  return !!(current && latest && compareFirmwareVersion(latest, current) > 0)
}

export function getTabsWithFirmwareUpdate(detail?: Record<string, unknown> | null, userUuid?: string | null): FirmwareTab[] {
  return FIRMWARE_TABS.filter(tab => hasFirmwareUpdate(tab, detail, userUuid))
}

export function hasAnyFirmwareUpdate(detail?: Record<string, unknown> | null, userUuid?: string | null): boolean {
  return getTabsWithFirmwareUpdate(detail, userUuid).length > 0
}

export { buildFirmwareUrl, FACTORY_ACCOUNT_UUID }
